import { Lock } from '@mui/icons-material';
import { Alert, Box, List, ListItem, ListItemText, TextField, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type React from 'react';
import { useState } from 'react';
import { sanitizeInput } from '../../utils/formUtils';

import WizardNavigation from './WizardNavigation';

interface Props {
  onNext: (data: { clientSecret: string; secretExpiryDate: string }) => void;
  onBack: () => void;
  activeStep: number;
  credentials: {
    clientSecret: string;
    secretExpiryDate?: string;
  };
}

const Step4ClientSecret: React.FC<Props> = ({ onNext, onBack, activeStep, credentials }) => {
  const theme = useTheme();
  const [clientSecret, setClientSecret] = useState(credentials.clientSecret || '');
  const [secretExpiryDate, setSecretExpiryDate] = useState(credentials.secretExpiryDate || '');

  const handleNext = () => {
    onNext({ clientSecret, secretExpiryDate });
  };

  const handleClientSecretChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setClientSecret(sanitizeInput(e.target.value));
  };

  const handleExpiryDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSecretExpiryDate(e.target.value);
  };

  return (
    <Box sx={{ textAlign: 'left' }}>
      <Typography variant="h6" gutterBottom>
        Step 4: Create a Client Secret & Grant Permissions
      </Typography>
      <List dense>
        <ListItem>
          <ListItemText primary={<b>Create a Client Secret:</b>} />
        </ListItem>
        <ListItem sx={{ pl: 2 }}>
          <ListItemText primary='– In the app’s menu, go to Manage then "Certificates & secrets".' />
        </ListItem>
        <ListItem sx={{ pl: 2 }}>
          <ListItemText primary='– Click "+ New client secret".' />
        </ListItem>
        <ListItem sx={{ pl: 2 }}>
          <ListItemText primary='– Add a description (e.g., "BIDLOGIQ Secret"), select an expiration period, and click Add.' />
        </ListItem>
        <ListItem sx={{ pl: 2 }}>
          <ListItemText
            primary={
              <span>
                – <b>IMPORTANT:</b> A secret will be generated. Immediately copy the <b>Value</b>{' '}
                and save it in a secure location. This is your Client Secret, and it will be hidden
                forever after you navigate away from this page.
              </span>
            }
          />
        </ListItem>
      </List>
      <TextField
        label="Client Secret"
        value={clientSecret}
        onChange={handleClientSecretChange}
        fullWidth
        required
        variant="outlined"
        margin="normal"
        type="password"
        sx={{ mt: 2 }}
      />
      <TextField
        label="Client Secret Expiration Date"
        value={secretExpiryDate}
        onChange={handleExpiryDateChange}
        fullWidth
        required
        variant="outlined"
        margin="normal"
        type="date"
        InputLabelProps={{
          shrink: true,
        }}
        sx={{ mt: 2 }}
      />
      <Alert severity="success" icon={<Lock />} sx={{ mt: 1 }}>
        For your security, your Client Secret is encrypted at rest using Google's Key Management
        Service (KMS) and is never stored in plain text.
      </Alert>
      <List dense>
        <ListItem>
          <ListItemText primary={<b>Grant API Permissions:</b>} />
        </ListItem>
        <ListItem sx={{ pl: 2 }}>
          <ListItemText primary='– Go to "Manage" > "API permissions" in the app’s menu.' />
        </ListItem>
        <ListItem sx={{ pl: 2 }}>
          <ListItemText primary='– Click "+ Add a permission", then select "Microsoft Graph".' />
        </ListItem>
        <ListItem sx={{ pl: 2 }}>
          <ListItemText primary='– Choose "Application permissions".' />
        </ListItem>
        <ListItem sx={{ pl: 2 }}>
          <ListItemText primary='– In the search box, type "Sites.Selected", click to expand the "Sites" row, check the box next to it, and click "Add permissions".' />
        </ListItem>
        <ListItem sx={{ pl: 2 }}>
          <ListItemText
            primary={
              <span>
                - <b>IMPORTANT:</b> Finally, click the "Grant admin consent" button at the top of
                the list and click confirm.
              </span>
            }
          />
        </ListItem>
        <ListItem sx={{ pl: 2 }}>
          <Alert
            severity="success"
            sx={{
              width: '100%',
              fontSize: '1rem',
              mt: 1,
              backgroundColor: theme.palette.bidlogiq.rust,
              border: '1px solid #000000',
              color: theme.palette.common.white,
              opacity: 0.7,
              '& .MuiAlert-icon': {
                color: theme.palette.common.white,
              },
            }}
          >
            <b>Verification:</b> After granting consent, ensure you see green checkmarks under the
            "Status" column for the <b>Sites.Selected</b> permission.
          </Alert>
        </ListItem>
      </List>
      <Typography
        variant="body1"
        sx={{
          mt: 2,
          p: 2,
          color: theme.palette.bidlogiq.black,
          backgroundColor: theme.palette.bidlogiq.taupe,
          borderRadius: 1,
          border: '1px solid #000000',
        }}
      >
        <b>Note on Sites.Selected:</b> This permission allows an app to access only the SharePoint
        site collections it has been explicitly granted permission to. The application will be
        blocked with a "forbidden" error if it attempts to access any other site.
      </Typography>
      <WizardNavigation
        activeStep={activeStep}
        onBack={onBack}
        onNext={handleNext}
        isNextDisabled={!clientSecret || !secretExpiryDate}
      />
    </Box>
  );
};

export default Step4ClientSecret;
